// extractions/reducer.js
import constants from "../../commonUtils/constants";
import * as actionTypes from "./actionTypes";
import { convertStringDataToTableJson, sanitizeExtractionData } from "../../commonUtils/extraction.helper";

const MAX_STEP = 4;

const initialState = {
  extLoading: false,
  fullDataLoading: false,
  stepVal: 1,
  filters: { keyword: "" },
  fullDataFilters: { keyword: "" },
  fieldValueOptionsMap: {},
  ExtractionsList: constants.extraction.listInitialState,
  FullDataList: constants.extraction.fullDataListInitialState,
  columnListValues: [],
  customColumnOptions: [],
  dataGroupStructure: [],
  pageLoadData: {
    masterProjects: [],
    projects: [],
    defaultColumnSelections: {
      selected_columns: [],
      custom_columns: []
    }
  },
  extractionData: constants.extraction.dataInitialState,
  projectsLoading: false,
  metaDataLoading: false,
  pincodeLoading: false,
  pincodeOptions: [],       // suggestions list for pincode autocomplete
  viewData: {},
  extErrors: constants.extraction.errorInitialState,
  qbErrors: {},
  pageLoadDataLoading: false,
  errorMessage: "",
  extractionViewOnly: false,
  ingestion: {
    progress: 0,
    action: "",
  }
};

const Extractions = (state = initialState, action) => {
    if (action.type === 'RESET_FULL_DATA_LIST') {
      return {
        ...state,
        FullDataList: constants.extraction.fullDataListInitialState,
        fullDataLoading: false,
        extErrors: constants.extraction.errorInitialState,
        errorMessage: "",
      };
    }
  switch (action.type) {
    case actionTypes.SET_EXT_LOADING:
      return { ...state, extLoading: action.payload };

    case actionTypes.SET_FULL_DATA_LOADING:
      return { ...state, fullDataLoading: action.payload };

    case actionTypes.START_SHOW_FULL_DATA:
      return { ...state, fullDataLoading: true, ingestion: { progress: 0, action: "" } };

    case actionTypes.SET_FILTERS:
      return { ...state, filters: action.payload };

    case actionTypes.SET_FULL_DATA_FILTERS:
      return { ...state, fullDataFilters: action.payload };

    case actionTypes.SET_PAGE_LOAD_DATA_LOADING:
      return { ...state, pageLoadDataLoading: action.payload };

    case actionTypes.SET_VIEW_MODE:
      return { ...state, extractionViewOnly: action.payload };

    case actionTypes.SET_EXT_ERRORS:
      return { ...state, extErrors: action.payload };

    case actionTypes.SET_QB_ERRORS:
      return { ...state, qbErrors: action.payload };

    case actionTypes.SET_EXTRACTION_DATA:
      return { ...state, extractionData: action.payload };

    case actionTypes.SET_STEP_VAL:
      return { ...state, stepVal: action.payload };

    case actionTypes.SET_VIEW_DATA: {
      const stucturedJson = convertStringDataToTableJson(action.payload);
      return { ...state, extLoading: false, viewData: stucturedJson };
    }

    case actionTypes.LOAD_EXT_PAGE_DATA_SUCCESS: {
      // If masterProjects is array of objects, convert to array of strings
      let incoming = { ...action.payload };
      if (Array.isArray(incoming.masterProjects) && incoming.masterProjects.length > 0 && typeof incoming.masterProjects[0] === 'object') {
        incoming.masterProjects = incoming.masterProjects.map(mp => mp.label || mp.value || mp.title || mp);
      }
      if (Array.isArray(incoming.projects) && incoming.projects.length > 0 && typeof incoming.projects[0] === 'object') {
        incoming.projects = incoming.projects.map(mp => mp.label || mp.value || mp.title || mp);
      }
      // Merge into existing pageLoadData so that projects set by GET_PROJECTS_FOR_MASTER_SUCCESS
      // or SET_PAGE_LOAD_PROJECTS are not wiped when page data reloads
      const pageLoadData = {
        ...state.pageLoadData,
        ...incoming,
        projects: incoming.projects ?? state.pageLoadData.projects,
      };
      return {
        ...state,
        extLoading: false,
        pageLoadData,
        extErrors: constants.extraction.errorInitialState,
        errorMessage: "",
      };
    }

    case actionTypes.LOAD_EXT_PAGE_DATA_FAILURE:
      return {
        ...state,
        extLoading: false,
        extErrors: action.payload.data,
        errorMessage: action.payload.message
      };

    case actionTypes.GET_EXTRACTIONS_SUCCESS:
      return { ...state, extLoading: false, ExtractionsList: action.payload };

    case actionTypes.GET_EXTRACTIONS_FAILURE:
      return {
        ...state,
        extLoading: false,
        extErrors: action.payload.data,
        errorMessage: action.payload.message
      };

    case actionTypes.CREATE_EXTRACTION_SUCCESS:
      return {
        ...state,
        extLoading: false,
        extErrors: constants.extraction.errorInitialState,
        stepVal: Math.min(parseInt(action.payload.workflow_step) + 1, MAX_STEP),
        errorMessage: "",
        extractionData: sanitizeExtractionData({ ...state.extractionData, ...action.payload })
      };

    case actionTypes.CREATE_EXTRACTION_FAILURE:
      return {
        ...state,
        extLoading: false,
        extErrors: action.payload.data,
        errorMessage: action.payload.message
      };

    case actionTypes.UPDATE_EXTRACTION_SUCCESS:
      return {
        ...state,
        extLoading: false,
        extErrors: constants.extraction.errorInitialState,
        errorMessage: "",
        extractionData: sanitizeExtractionData({ ...state.extractionData, ...action.payload })
      };

    case actionTypes.UPDATE_EXTRACTION_FAILURE:
      return {
        ...state,
        extLoading: false,
        extErrors: action.payload.data || {},
        errorMessage: action.payload.message
      };

    case actionTypes.LOAD_EXTRACTION_SUCCESS: {
      const extData = sanitizeExtractionData({ ...state.extractionData, ...action.payload });
      return {
        ...state,
        extLoading: false,
        extErrors: constants.extraction.errorInitialState,
        stepVal: action?.setStepVal || Math.min(parseInt(action.payload.workflow_step) + 1, MAX_STEP),
        errorMessage: "",
        extractionData: extData
      };
    }

    case actionTypes.LOAD_EXTRACTION_FAILURE:
      return {
        ...state,
        extLoading: false,
        extErrors: action.payload.data,
        errorMessage: action.payload.message
      };

    case actionTypes.GET_COLUMN_LIST_VALUES_SUCCESS:
      return { ...state, extLoading: false, columnListValues: action.payload };

    case actionTypes.GET_COLUMN_LIST_VALUES_FAILURE:
      return {
        ...state,
        extLoading: false,
        extErrors: action.payload.data,
        errorMessage: action.payload.message
      };

    case actionTypes.GET_FIELD_VALUE_OPTIONS:
      return {
        ...state,
        extLoading: false,
        fieldValueOptionsMap: {
          ...state.fieldValueOptionsMap,
          [action.payload.field]: {
            extLoading: true,
            values: state.fieldValueOptionsMap[action.payload.field]?.values || [],
            error: null
          }
        }
      };

    case actionTypes.GET_FIELD_VALUE_OPTIONS_SUCCESS:
      return {
        ...state,
        extLoading: false,
        extErrors: constants.extraction.errorInitialState,
        errorMessage: "",
        fieldValueOptionsMap: {
          ...state.fieldValueOptionsMap,
          [action.payload.field]: {
            extLoading: false,
            values: action.payload.values || [],
            error: null
          }
        }
      };

    case actionTypes.GET_FIELD_VALUE_OPTIONS_FAILURE:
      return {
        ...state,
        extLoading: false,
        extErrors: action.payload.data,
        errorMessage: action.payload.message,
        fieldValueOptionsMap: {
          ...state.fieldValueOptionsMap,
          [action.payload.field]: {
            extLoading: false,
            values: [],
            error: action.payload.message || "Failed to load options"
          }
        }
      };

    case actionTypes.RESET_QUERY_BUILDER:
      return { ...state, extLoading: false, fieldValueOptionsMap: {} };

    case actionTypes.GET_PROJECTS_FOR_MASTER_SUCCESS:
      return {
        ...state,
        projectsLoading: false,
        pageLoadData: { ...state.pageLoadData, projects: action.payload }
      };

    case actionTypes.SET_PAGE_LOAD_PROJECTS:
      return {
        ...state,
        pageLoadData: { ...state.pageLoadData, projects: action.payload }
      };

    case actionTypes.GET_PROJECTS_FOR_MASTER_FAILURE:
      return {
        ...state,
        projectsLoading: false,
        extErrors: action.payload.data,
        errorMessage: action.payload.message,
      };

    case actionTypes.GET_EXT_PROJECTS_FOR_MASTER_LOADING:
      return {
        ...state,
        projectsLoading: action.payload
      };

    case actionTypes.GET_EXT_META_DATA_LOADING:
      return {
        ...state,
        metaDataLoading: action.payload
      };

    case actionTypes.GET_EXT_PINCODE_FROM_MASTER_LOADING:
      return {
        ...state,
        pincodeLoading: action.payload
      };

    case actionTypes.GET_EXT_META_DATA_SUCCESS: {
      const rawPrice = action.payload.approximateStartingPrice;
      const rawStr = String(rawPrice ?? '').trim();
      const isExponential = /[eE]/.test(rawStr);
      const parsedPrice = parseFloat(rawStr);
      const safePrice = !isExponential && !isNaN(parsedPrice) ? String(parsedPrice) : "0";

      return {
          ...state,
          metaDataLoading: false,
          extractionData: {
              ...state.extractionData,
              approxStartingPrice: safePrice,
              pincode: action.payload.pincode   // pre-fill pincode from metadata
          }
      };
    }

    case actionTypes.GET_EXT_META_DATA_FAILURE:
      return {
        ...state,
        metaDataLoading: false,
        extErrors: action.payload.data,
        errorMessage: action.payload.message,
      };

    // Stores the search suggestions — does NOT overwrite extractionData.pincode
    case actionTypes.GET_EXT_PINCODE_FROM_MASTER_SUCCESS:
      return {
        ...state,
        pincodeLoading: false,
        pincodeOptions: action.payload,   // array of pincode strings for the dropdown
      };

    case actionTypes.GET_EXT_PINCODE_FROM_MASTER_FAILURE:
      return {
        ...state,
        pincodeLoading: false,
        pincodeOptions: [],
        extErrors: action.payload.data,
        errorMessage: action.payload.message,
      };

    case actionTypes.NEXT_WORKFLOW_STEP_SUCCESS: {
      const updated_workflow_step = action.payload.updated_workflow_step;
      return {
        ...state,
        extLoading: false,
        stepVal: Math.min(updated_workflow_step + 1, MAX_STEP),
        extractionData: { ...state.extractionData, workflow_step: updated_workflow_step }
      };
    }

    case actionTypes.NEXT_WORKFLOW_STEP_FAILURE:
      return {
        ...state,
        extLoading: false,
        extErrors: action.payload.data,
        errorMessage: action.payload.message,
      };

    case actionTypes.MOVE_TO_STEP3_SUCCESS:
      return { ...state, extLoading: false, stepVal: 3 };

    case actionTypes.MOVE_TO_STEP3_FAILURE:
      return {
        ...state,
        extLoading: false,
        extErrors: action.payload.data,
        errorMessage: action.payload.message,
      };

    case actionTypes.LOAD_CUSTOM_COLUMN_OPTIONS_SUCCESS:
      return { ...state, extLoading: false, customColumnOptions: action.payload };

    case actionTypes.LOAD_CUSTOM_COLUMN_OPTIONS_FAILURE:
      return { ...state, extLoading: false };

    // payload shape: { data: { data: [...], meta: {...}, pagination: { currentPage, perPage, total, totalPages } }, endpoint: '...' }
    case actionTypes.VIEW_FULL_DATA_SUCCESS: {
      const respData   = action.payload.data;
      const endpoint   = action.payload.endpoint;

      const pagination = respData?.pagination
        ?? (endpoint === '/parquet-ingest/start'
            ? constants.extraction.fullDataListInitialState.pagination
            : state.FullDataList.pagination);

      const meta = {
        ...(respData?.meta || {}),
        extOutputMappingId: respData?.meta?.extOutputMappingId || respData?.meta?._id || respData?.extOutputMappingId || state.FullDataList?.meta?.extOutputMappingId
      };

      return {
        ...state,
        fullDataLoading: false,
        FullDataList: {
          data:       respData?.data       || [],
          meta,
          pagination,
        },
        extErrors: constants.extraction.errorInitialState,
        errorMessage: "",
      };
    }

    case actionTypes.VIEW_FULL_DATA_FAILURE:
      return {
        ...state,
        fullDataLoading: false,
        extErrors: action.payload?.data || {},
        errorMessage: action.payload?.message || "Failed to load full data",
      };

    case actionTypes.SET_SELECTED_VERSION_SUCCESS:
      return {
        ...state,
        extLoading: false,
        extractionData: sanitizeExtractionData({
          ...state.extractionData,
          workflow_step: 4,
          selected_version: action.payload.selected_version
        }),
        extErrors: constants.extraction.errorInitialState,
        errorMessage: "",
      };

    case actionTypes.SET_SELECTED_VERSION_FAILURE:
      return {
        ...state,
        extLoading: false,
        extErrors: action.payload?.data || {},
        errorMessage: action.payload?.message || "Failed to update selected version",
      };

    case actionTypes.SET_INGESTION_PROGRESS:
      return { ...state, ingestion: action.payload };

    default:
      return state;
  }
};

export default Extractions;